import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Gift, Copy, Ticket, ShoppingBag, Heart, Mail, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { DEFAULT_PRICING, formatPrice, hasPromo, fetchPricing } from "@/lib/pricing";
import { useAuth } from "@/context/AuthContext";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { AdventCalendar } from "@/components/AdventCalendar";

export default function Dashboard() {  const { user } = useAuth();
  const [calendars, setCalendars] = useState(null);
  const [orders, setOrders] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [giftOrder, setGiftOrder] = useState(null);
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [giftMessage, setGiftMessage] = useState("");
  const [editions, setEditions] = useState([]);
  const [editionId, setEditionId] = useState("classico");
  const [pricing, setPricing] = useState(DEFAULT_PRICING);
  const [giftPurchaseOpen, setGiftPurchaseOpen] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);

  const resendVerification = async () => {
    setResendBusy(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success("Email di conferma inviata! Controlla la tua casella di posta.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResendBusy(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const [cals, ords, eds] = await Promise.all([
        api.get("/calendars/my"),
        api.get("/orders/my"),
        api.get("/editions"),
      ]);
      setCalendars(cals.data);
      setOrders(ords.data);
      setEditions(eds.data);
      if (cals.data.length && !selectedId) setSelectedId(cals.data[0].id);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [selectedId]);

  useEffect(() => {
    load();
    fetchPricing().then(setPricing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buy = async (kind) => {
    setBusy(true);
    try {
      const { data } = await api.post("/orders", { kind, edition_id: editionId });
      if (kind === "gift") {
        toast.success(`Codice regalo creato: ${data.code}`);
      } else {
        toast.success("Il tuo calendario è attivo! Buon Avvento 🎄");
      }
      setSelectedId(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const redeem = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      await api.post("/calendars/redeem", { code: code.trim() });
      toast.success("Calendario attivato! Le caselle ti aspettano ✨");
      setCode("");
      setSelectedId(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err, "Codice non valido."));
    } finally {
      setBusy(false);
    }
  };

  const openDoor = async (day) => {
    const { data } = await api.post(`/calendars/${selectedId}/open?day=${day}`);
    if (!data.already && data.prize_email_sent) {
      toast.success("Ti abbiamo inviato la sorpresa anche via email 📬");
    }
    await load();
    return data.reward;
  };

  const copyCode = (value) => {
    navigator.clipboard?.writeText(value);
    toast.success("Codice copiato!");
  };

  const sendGift = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${giftOrder.id}/send-gift`, {
        recipient_email: recipientEmail,
        recipient_name: recipientName,
        message: giftMessage,
      });
      toast.success(`Codice regalo inviato a ${data.sent_to} 🎁`);
      setGiftOrder(null);
      setRecipientName("");
      setRecipientEmail("");
      setGiftMessage("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const purchaseGift = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/orders", {
        kind: "gift",
        edition_id: editionId,
        recipient_email: recipientEmail,
        recipient_name: recipientName,
        gift_message: giftMessage,
      });
      if (data.gift_sent_to) {
        toast.success(`Regalo inviato a ${data.gift_sent_to} 🎁 Ci abbiamo pensato noi!`);
      } else {
        toast.warning(`Codice ${data.code} creato, ma l'invio non è riuscito: riprova dalla lista dei tuoi codici.`);
      }
      setGiftPurchaseOpen(false);
      setRecipientName("");
      setRecipientEmail("");
      setGiftMessage("");
      setSelectedId(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const closeGiftModal = () => {
    setGiftOrder(null);
    setGiftPurchaseOpen(false);
  };

  const selected = calendars?.find((c) => c.id === selectedId);
  const liveDoors = selected
    ? selected.doors.map((d) => ({
        day: d.day,
        isLocked: !d.unlocked,
        isDemo: false,
        isOpened: d.opened,
        reward: null,
        unlockDate: null,
      }))
    : [];

  return (
    <div className="relative min-h-screen bg-[#06150F] text-[#FAF6EE]">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[420px] bg-[radial-gradient(ellipse_at_top,rgba(90,15,25,0.5),transparent_65%)]"
      />
      <SiteHeader onBuy={() => document.getElementById("acquista")?.scrollIntoView({ behavior: "smooth" })} />

      <main className="relative z-10 mx-auto max-w-6xl px-5 pb-24 pt-10 sm:px-8">
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <h1 data-testid="dashboard-title" className="font-display text-3xl font-bold text-amber-100 sm:text-4xl">
            Ciao, {user.name} ✨
          </h1>
          <p className="mt-2 text-sm text-emerald-100/70 sm:text-base">
            Questa è la tua area personale: qui apri le caselle, gestisci i regali e i tuoi codici.
          </p>
        </motion.div>

        {user.email_verified === false && (
          <div
            data-testid="email-verify-banner"
            className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-amber-400/50 bg-amber-500/10 px-5 py-4"
          >
            <div className="flex items-start gap-3">
              <Mail className="mt-0.5 h-5 w-5 shrink-0 text-amber-300" />
              <p className="text-sm leading-relaxed text-amber-100/90">
                <span className="font-bold">Conferma la tua email</span> per aprire le caselle del
                calendario: ti abbiamo inviato un link di conferma al tuo indirizzo.
              </p>
            </div>
            <button
              type="button"
              data-testid="resend-verification-button"
              onClick={resendVerification}
              disabled={resendBusy}
              className="rounded-xl border border-amber-400/60 bg-amber-400/15 px-4 py-2 text-xs font-bold uppercase tracking-wider text-amber-200 transition-colors hover:bg-amber-400/25 disabled:opacity-60"
            >
              {resendBusy ? "Invio..." : "Reinvia email"}
            </button>
          </div>
        )}

        {calendars === null ? (
          <div className="mt-16 flex justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-amber-400/30 border-t-amber-400" />
          </div>
        ) : (
          <div className="mt-10 space-y-12">
            {/* purchase / redeem */}
            {editions.length > 1 && (
              <div className="mb-5 flex flex-wrap items-center gap-3" data-testid="edition-picker">
                <span className="text-sm font-semibold text-amber-200/90">Scegli l'edizione del calendario:</span>
                <select
                  data-testid="edition-select"
                  value={editionId}
                  onChange={(e) => setEditionId(e.target.value)}
                  className="rounded-xl border border-amber-500/25 bg-[#0A1D15]/80 px-4 py-2.5 text-sm text-[#FAF6EE] outline-none focus:border-amber-400/70"
                >
                  {editions.map((e) => (
                    <option key={e.id} value={e.id}>{e.name}</option>
                  ))}
                </select>
              </div>
            )}
            <section id="acquista" className="grid gap-5 md:grid-cols-3">
              <div className="rounded-2xl border border-amber-500/25 bg-gradient-to-b from-[#0E2A1E] to-[#081C13] p-6" data-testid="buy-self-card">
                <ShoppingBag className="h-6 w-6 text-amber-300" />
                <h3 className="mt-3 font-display text-xl font-bold text-amber-100">Per te</h3>
                <p className="mt-1.5 text-sm text-emerald-100/70">
                  Attiva subito il tuo calendario personale.
                </p>
                <p className="mt-3 flex items-baseline gap-2">
                  {hasPromo(pricing) && (
                    <span className="text-sm text-emerald-100/45 line-through">{formatPrice(pricing.original_price)}</span>
                  )}
                  <span className="font-display text-2xl font-bold gold-text">{formatPrice(pricing.price)}</span>
                </p>
                <button
                  type="button"
                  data-testid="buy-self-button"
                  disabled={busy}
                  onClick={() => buy("self")}
                  className="mt-4 w-full rounded-xl bg-gradient-to-r from-amber-500 via-amber-400 to-yellow-600 px-5 py-3 font-bold text-[#1A1204] transition-all duration-300 hover:scale-[1.02] disabled:opacity-60"
                >
                  Compra per te
                </button>
              </div>

              <div className="rounded-2xl border border-red-400/30 bg-gradient-to-b from-[#2A0B12] to-[#160509] p-6" data-testid="buy-gift-card">
                <Heart className="h-6 w-6 text-red-300" />
                <h3 className="mt-3 font-display text-xl font-bold text-amber-100">Come regalo</h3>
                <p className="mt-1.5 text-sm text-emerald-100/70">
                  Scrivi nome, email e un messaggio: la piattaforma invia il regalo al destinatario per te.
                </p>
                <p className="mt-3 flex items-baseline gap-2">
                  {hasPromo(pricing) && (
                    <span className="text-sm text-emerald-100/45 line-through">{formatPrice(pricing.original_price)}</span>
                  )}
                  <span className="font-display text-2xl font-bold gold-text">{formatPrice(pricing.price)}</span>
                </p>
                <button
                  type="button"
                  data-testid="buy-gift-button"
                  disabled={busy}
                  onClick={() => setGiftPurchaseOpen(true)}
                  className="mt-4 w-full rounded-xl border border-red-400/50 bg-[#4A0D16] px-5 py-3 font-bold text-red-100 transition-all duration-300 hover:scale-[1.02] hover:bg-[#5E121D] disabled:opacity-60"
                >
                  Regala e invia subito
                </button>
              </div>

              <div className="rounded-2xl border border-emerald-400/25 bg-gradient-to-b from-[#0C241A] to-[#081C13] p-6" data-testid="redeem-card">
                <Ticket className="h-6 w-6 text-emerald-300" />
                <h3 className="mt-3 font-display text-xl font-bold text-amber-100">Hai un codice?</h3>
                <p className="mt-1.5 text-sm text-emerald-100/70">
                  Ti hanno regalato un calendario? Attivalo qui.
                </p>
                <form onSubmit={redeem} className="mt-4 flex gap-2">
                  <input
                    data-testid="redeem-code-input"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="AVV-XXXXXXXX"
                    className="w-full rounded-xl border border-amber-500/25 bg-[#0A1D15]/80 px-4 py-3 text-sm uppercase tracking-wider text-[#FAF6EE] placeholder:text-emerald-100/40 outline-none focus:border-amber-400/70"
                  />
                  <button
                    type="submit"
                    data-testid="redeem-submit-button"
                    disabled={busy}
                    className="shrink-0 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-600 px-4 py-3 text-sm font-bold text-[#1A1204] disabled:opacity-60"
                  >
                    Attiva
                  </button>
                </form>
              </div>
            </section>

            {/* calendars */}
            {calendars.length > 0 && selected && (
              <section data-testid="my-calendar-section">
                {calendars.length > 1 && (
                  <div className="mb-6 flex flex-wrap gap-2">
                    {calendars.map((cal) => (
                      <button
                        key={cal.id}
                        type="button"
                        data-testid={`calendar-tab-${cal.id}`}
                        onClick={() => setSelectedId(cal.id)}
                        className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                          cal.id === selectedId
                            ? "border-amber-400/70 bg-amber-400/15 text-amber-200"
                            : "border-amber-500/20 text-emerald-100/60 hover:text-amber-200"
                        }`}
                      >
                        {cal.label} · {cal.code}
                      </button>
                    ))}
                  </div>
                )}
                <div className="mx-auto max-w-4xl">
                  <AdventCalendar
                    doors={liveDoors}
                    mode="live"
                    onOpenDoor={openDoor}
                    onError={(e) => toast.error(formatApiError(e))}
                  />
                  <p className="mt-5 text-center text-sm text-emerald-100/70">
                    Le caselle si aprono dal 1 dicembre: ogni giorno sblocca la sua sorpresa.
                  </p>
                </div>
              </section>
            )}

            {/* gift codes */}
            {orders.length > 0 && (
              <section data-testid="my-orders-section">
                <h2 className="font-display text-2xl font-bold text-amber-100">I tuoi codici</h2>
                <div className="mt-5 space-y-3">
                  {orders.map((order) => (
                    <div
                      key={order.id}
                      data-testid={`order-row-${order.code}`}
                      className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-500/20 bg-[#0C241A]/60 px-5 py-4"
                    >
                      <div className="flex items-center gap-3">
                        <Gift className="h-5 w-5 text-amber-300" />
                        <div>
                          <p className="font-mono text-sm font-bold tracking-wider text-amber-100">{order.code}</p>
                          <p className="text-xs text-emerald-100/60">
                            {order.kind === "gift" ? "Regalo" : "Per te"} · € {order.price.toFixed(2).replace(".", ",")} ·{" "}
                            {new Date(order.created_at).toLocaleDateString("it-IT")}
                            {order.gift_recipient && ` · Inviato a ${order.gift_recipient}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span
                          className={`rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wider ${
                            order.redeemed_by
                              ? "bg-emerald-400/15 text-emerald-300"
                              : "bg-amber-400/15 text-amber-300"
                          }`}
                        >
                          {order.redeemed_by ? "Attivato" : "Da attivare"}
                        </span>
                        {order.kind === "gift" && !order.redeemed_by && (
                          <button
                            type="button"
                            data-testid={`send-gift-${order.code}`}
                            onClick={() => setGiftOrder(order)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-red-400/40 bg-[#4A0D16]/60 px-3 py-1.5 text-xs font-semibold text-red-100 transition-colors hover:bg-[#5E121D]"
                          >
                            <Mail className="h-3.5 w-3.5" /> Invia al destinatario
                          </button>
                        )}
                        <button
                          type="button"
                          data-testid={`copy-code-${order.code}`}
                          onClick={() => copyCode(order.code)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-amber-400/40 px-3 py-1.5 text-xs font-semibold text-amber-200 transition-colors hover:bg-amber-400/10"
                        >
                          <Copy className="h-3.5 w-3.5" /> Copia
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </main>

      {/* gift email modal */}
      <AnimatePresence>
        {(giftOrder || giftPurchaseOpen) && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div
              className="absolute inset-0 bg-[#030B08]/80 backdrop-blur-sm"
              onClick={closeGiftModal}
            />
            <motion.div
              data-testid="gift-modal"
              initial={{ opacity: 0, y: 32, scale: 0.94 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-md rounded-2xl border border-amber-400/40 bg-gradient-to-b from-[#112A1E] to-[#0A1D15] p-7 shadow-[0_0_70px_rgba(212,175,55,0.25)] sm:p-8"
            >
              <button
                type="button"
                data-testid="gift-modal-close"
                aria-label="Chiudi"
                onClick={closeGiftModal}
                className="absolute right-3 top-3 rounded-full p-1.5 text-emerald-100/60 transition-colors hover:bg-white/5 hover:text-amber-200"
              >
                <X className="h-4 w-4" />
              </button>
              <h3 className="font-display text-2xl font-bold text-amber-100">
                {giftPurchaseOpen ? "Regala il calendario: ci pensiamo noi" : "Invia il regalo via email"}
              </h3>
              <p className="mt-2 text-sm text-emerald-100/70">
                {giftPurchaseOpen ? (
                  <>
                    Inserisci i dati del destinatario: con l'acquisto gli invieremo subito un'email
                    con il codice di attivazione e il tuo messaggio.
                  </>
                ) : (
                  <>
                    Il destinatario riceverà il codice{" "}
                    <span className="font-mono font-bold text-amber-200">{giftOrder?.code}</span> con le
                    istruzioni per attivarlo.
                  </>
                )}
              </p>
              <form onSubmit={giftPurchaseOpen ? purchaseGift : sendGift} className="mt-6 space-y-4" data-testid="gift-form">
                <input
                  data-testid="gift-name-input"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  placeholder="Nome del destinatario (opzionale)"
                  className="w-full rounded-xl border border-amber-500/25 bg-[#0A1D15]/80 px-4 py-3 text-sm text-[#FAF6EE] placeholder:text-emerald-100/40 outline-none focus:border-amber-400/70"
                />
                <input
                  type="email"
                  required
                  data-testid="gift-email-input"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  placeholder="Email del destinatario"
                  className="w-full rounded-xl border border-amber-500/25 bg-[#0A1D15]/80 px-4 py-3 text-sm text-[#FAF6EE] placeholder:text-emerald-100/40 outline-none focus:border-amber-400/70"
                />
                <textarea
                  rows={3}
                  data-testid="gift-message-input"
                  value={giftMessage}
                  onChange={(e) => setGiftMessage(e.target.value)}
                  placeholder="Un messaggio personale (opzionale)"
                  className="w-full resize-none rounded-xl border border-amber-500/25 bg-[#0A1D15]/80 px-4 py-3 text-sm text-[#FAF6EE] placeholder:text-emerald-100/40 outline-none focus:border-amber-400/70"
                />
                <button
                  type="submit"
                  data-testid="gift-send-button"
                  disabled={busy}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 via-amber-400 to-yellow-600 px-6 py-3.5 font-bold text-[#1A1204] shadow-lg transition-all duration-300 hover:scale-[1.02] disabled:opacity-60"
                >
                  <Mail className="h-4 w-4" />{" "}
                  {busy
                    ? "Invio in corso..."
                    : giftPurchaseOpen
                      ? `Compra e invia · ${formatPrice(pricing.price)}`
                      : "Invia il regalo"}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <SiteFooter />
    </div>
  );
}
